import { useTranslation } from 'react-i18next';
/**
 * QrLabelDialog — print a QR sticker for one record, or a batch of them.
 *
 * Single record (``id``): live preview of the actual sticker, the public
 * link it opens, and the controls that govern that link (public on/off,
 * rotate = invalidate every sticker already printed).
 *
 * Batch (``ids``): size + layout only, straight to a PDF.
 *
 * ``roll`` layout emits one page per label at the exact sticker size —
 * that is what a portable printer (Niimbot B21 etc.) expects. ``sheet``
 * tiles them on A4.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Copy, Download, ExternalLink, Loader2, Printer, RefreshCw, QrCode,
} from 'lucide-react';
import { LABEL_PRESETS, downloadLabelPng, fetchLabelPng, openLabelsPdf, sheetCapacity } from '../lib/qrLabels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QrLabelDialog = ({
  open, onOpenChange, kind, id, ids, isAdmin = false, title: heading,
}) => {
  const { t } = useTranslation();
  const batch = Array.isArray(ids) && ids.length > 0;
  const targets = batch ? ids : [id];
  const [info, setInfo] = useState(null);
  const [preset, setPreset] = useState('50x30');
  const [layout, setLayout] = useState('roll');
  const [copies, setCopies] = useState(1);
  const [logo, setLogo] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [showBase, setShowBase] = useState(false);

  useEffect(() => {
    if (!open || batch) return;
    axios.get(`${API}/qr/${kind}/${id}`)
      .then((r) => setInfo(r.data))
      .catch(() => toast.error(t('auto.misc.couldNotPrepareTheQrCode')));
  }, [open, batch, kind, id]);

  const refreshPreview = useCallback(async () => {
    if (!open || batch) return;
    try {
      const blob = await fetchLabelPng({ kind, id, preset, logo, dpi: 300 });
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    } catch { /* preview is a nicety, never block the dialog */ }
  }, [open, batch, kind, id, preset, logo]);

  useEffect(() => { refreshPreview(); }, [refreshPreview]);

  const print = async () => {
    setBusy(true);
    try {
      await openLabelsPdf({ kind, ids: targets, preset, layout, logo, copies });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not build the label PDF');
    }
    setBusy(false);
  };

  const download = async () => {
    setBusy(true);
    try {
      await downloadLabelPng({ kind, id, preset, code: info?.code, logo });
      toast.success(t('auto.misc.pngSavedOpenItInYour'));
    } catch { toast.error(t('auto.misc.couldNotExportTheLabel')); }
    setBusy(false);
  };

  const rotate = async () => {
    if (!window.confirm(t('auto.misc.issueANewCodeEverySticker'))) return;
    try {
      const r = await axios.post(`${API}/qr/${kind}/${id}/rotate`);
      setInfo((p) => ({ ...p, ...r.data }));
      refreshPreview();
      toast.success(t('auto.misc.newCodeIssuedReprintTheSticker'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not rotate the code'); }
  };

  const togglePublic = async (enabled) => {
    try {
      await axios.put(`${API}/qr/${kind}/${id}/public`, { enabled });
      setInfo((p) => ({ ...p, public_enabled: enabled }));
      toast.success(enabled ? t('auto.misc.publicPageEnabled') : t('auto.misc.publicPageTurnedOff'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change public access'); }
  };

  const saveBase = async () => {
    try {
      await axios.put(`${API}/settings/qr-base-url`, { public_qr_base_url: baseUrl });
      toast.success(t('auto.misc.savedNewStickersWillUseThis'));
      const r = await axios.get(`${API}/qr/${kind}/${id}`);
      setInfo(r.data);
      refreshPreview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the address'); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.public_url);
      toast.success(t('auto.misc.linkCopied'));
    } catch { toast.error(t('auto.misc.copyFailedLongPressTheLink')); }
  };

  const stickerPreset = LABEL_PRESETS.find((p) => p.value === preset)?.sticker;
  const fit = sheetCapacity(preset);
  const totalStickers = targets.length * copies;
  const sheets = Math.ceil(totalStickers / fit.total);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="qr-label-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <QrCode className="w-4 h-4 text-[#AA0000]" />
            {batch ? `QR labels — ${targets.length} selected` : `QR label — ${heading || info?.title || ''}`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('auto.misc.scanningTheStickerOpensARead')}
          </DialogDescription>
        </DialogHeader>

        {!batch && (
          <div className="rounded border bg-slate-50 p-3 flex items-center justify-center min-h-[120px]">
            {preview
              ? <img src={preview} alt={t('auto.misc.labelPreview')} data-testid="qr-label-preview"
                  className="max-w-full max-h-40 object-contain bg-white shadow-sm" />
              : <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t('auto.misc.labelSize')}</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger className="h-9" data-testid="qr-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LABEL_PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t('auto.misc.printLayout')}</Label>
            <Select value={layout} onValueChange={setLayout}>
              <SelectTrigger className="h-9" data-testid="qr-layout-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="roll">{t('auto.misc.oneLabelPerPageStickerPrinter')}</SelectItem>
                <SelectItem value="sheet">{t('auto.misc.tiledOnA4Sheets')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {layout === 'sheet' && (
          <div className="rounded border px-3 py-2 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Label htmlFor="qr-copies" className="text-sm font-normal">{t('auto.misc.copiesOfEachLabel')}</Label>
                <p className="text-xs text-slate-500">
                  {preset.replace('x', ' × ')} mm fits {fit.total} per A4 sheet ({fit.cols} × {fit.rows}).
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Input id="qr-copies" type="number" min={1} max={200} value={copies}
                  onChange={(e) => setCopies(Math.min(200, Math.max(1, Number(e.target.value) || 1)))}
                  className="h-9 w-20 text-center" data-testid="qr-copies-input" />
                <Button type="button" variant="outline" className="h-9 shrink-0 text-xs"
                  onClick={() => setCopies(Math.max(1, Math.floor(fit.total / targets.length) || 1))}
                  data-testid="qr-fill-sheet-btn">{t('auto.misc.fillSheet')}</Button>
              </div>
            </div>
            <p className="text-xs text-slate-500" data-testid="qr-sheet-summary">
              {totalStickers} sticker{totalStickers === 1 ? '' : 's'} over {sheets} A4 sheet{sheets === 1 ? '' : 's'}.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between rounded border px-3 py-2">
          <Label htmlFor="qr-logo" className="text-sm font-normal">{t('auto.misc.printCompanyLogoOnTheLabel')}</Label>
          <Switch id="qr-logo" checked={logo} onCheckedChange={setLogo} data-testid="qr-logo-switch" />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={print} disabled={busy} data-testid="qr-print-btn" className="flex-1 min-w-[140px]">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            Print {layout === 'sheet' && totalStickers > 1
              ? `${totalStickers} labels`
              : (batch ? `${targets.length} labels` : 'label')}
          </Button>
          {!batch && (
            <Button variant="outline" onClick={download} disabled={busy} data-testid="qr-png-btn"
              className="flex-1 min-w-[140px]">
              <Download className="w-4 h-4 mr-2" /> {t('auto.misc.savePng')}
            </Button>
          )}
        </div>
        {stickerPreset && (
          <p className="text-xs text-slate-500 -mt-1">
            For a Niimbot B21 and similar: <strong>{t('auto.misc.savePng')}</strong>, then print that image from the
            printer&apos;s own app at {preset.replace('x', ' × ')} mm.
          </p>
        )}

        {!batch && info && (
          <div className="space-y-3 border-t pt-3">
            <div>
              <Label className="text-xs">{t('auto.misc.publicLinkThisQrOpens')}</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={info.public_url || ''} className="h-9 text-xs" data-testid="qr-public-url" />
                <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={copy} title={t('auto.misc.copyLink')}
                  data-testid="qr-copy-btn"><Copy className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" title={t('auto.misc.openPage')}
                  onClick={() => window.open(info.preview_url || info.public_url, '_blank')} data-testid="qr-open-btn">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between rounded border px-3 py-2">
              <div className="min-w-0 pr-2">
                <Label htmlFor="qr-public" className="text-sm font-normal">{t('auto.misc.anyoneWithTheCodeCanView')}</Label>
                <p className="text-xs text-slate-500">{t('auto.misc.turnOffToMakeTheSticker')}</p>
              </div>
              <Switch id="qr-public" checked={info.public_enabled !== false}
                onCheckedChange={togglePublic} data-testid="qr-public-switch" />
            </div>

            {isAdmin && (
              <div>
                <button type="button" onClick={() => { setShowBase((v) => !v); setBaseUrl(''); }}
                  className="text-xs text-[#AA0000] hover:underline" data-testid="qr-base-toggle">
                  {showBase ? t('auto.misc.hide') : t('auto.misc.wrongAddressInTheLink')}
                </button>
                {showBase && (
                  <div className="mt-2 space-y-2">
                    <p className="text-xs text-slate-500">
                      Set the address stickers should point at (your live domain). Do this before a print run —
                      existing stickers keep the address they were printed with.
                    </p>
                    <div className="flex gap-2">
                      <Input placeholder="https://erp.yourcompany.com" value={baseUrl}
                        onChange={(e) => setBaseUrl(e.target.value)} className="h-9 text-xs"
                        data-testid="qr-base-input" />
                      <Button variant="outline" className="h-9 shrink-0" onClick={saveBase}
                        data-testid="qr-base-save">{t('auto.misc.save')}</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button variant="ghost" size="sm" onClick={rotate} className="text-slate-500 hover:text-red-600"
              data-testid="qr-rotate-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-2" /> {t('auto.misc.issueANewCode')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QrLabelDialog;
